import { ChevronRight, Search, Users } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { cn, formatCurrency } from '@/lib/utils';

const formatCompactDate = (value, includeYear = true) => {
  if (!value) return '—';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  if (year && month && day) {
    return includeYear ? `${day}/${month}/${year.slice(-2)}` : `${day}/${month}`;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  const parsedDay = String(parsed.getDate()).padStart(2, '0');
  const parsedMonth = String(parsed.getMonth() + 1).padStart(2, '0');
  const parsedYear = String(parsed.getFullYear()).slice(-2);
  return includeYear ? `${parsedDay}/${parsedMonth}/${parsedYear}` : `${parsedDay}/${parsedMonth}`;
};

export const PayrollEmployeeDirectorySection = ({
  sortedEmployees,
  employeeSortField,
  employeeSortDirection,
  employeeSortOptions,
  employeeDirectory,
  selectedEmployeeIds,
  allVisibleEmployeesSelected,
  canManagePayroll,
  openPayrollEmployeeDialog,
  toggleAllVisibleEmployees,
  handleBulkProcessEmployees,
  toggleEmployeeSelection,
  openDossierDialog,
  employeeSearchQuery,
  onEmployeeSearchChange,
}) => (
  <Card className="overflow-hidden rounded-2xl border-slate-200 shadow-sm" data-testid="payroll-employees-list-card">
    <CardHeader className="border-b border-slate-200 bg-white/90 px-3 py-3 md:px-4">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base md:text-lg">
            <Users className="h-5 w-5" />
            Dossier digital ({sortedEmployees.length})
          </CardTitle>
          <p className="text-xs text-slate-500" data-testid="payroll-employees-directory-description">
            Lista super ultracompacta iniciando em 001, com leitura rápida para RH.
          </p>
        </div>

        <div className="flex w-full flex-col gap-2 xl:w-auto xl:items-end">
          <div className="flex flex-wrap items-center justify-end gap-1.5" data-testid="payroll-employees-bulk-toolbar">
            <div className="relative min-w-[220px] flex-1 xl:w-[270px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Pesquisar funcionário..."
                value={employeeSearchQuery}
                onChange={(event) => onEmployeeSearchChange(event.target.value)}
                className="h-8 rounded-lg border-slate-200 bg-slate-50 pl-8 text-xs"
                data-testid="payroll-employee-search-input"
              />
            </div>
            {selectedEmployeeIds.length > 0 && (
              <Badge variant="outline" className="bg-white" data-testid="payroll-employees-selection-badge">{selectedEmployeeIds.length} seleccionado(s)</Badge>
            )}
            {canManagePayroll && (
              <Button variant="outline" size="sm" className="h-8 rounded-lg px-2.5 text-xs" onClick={openPayrollEmployeeDialog} data-testid="payroll-create-employee-button">
                Novo colaborador RH
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-8 rounded-lg px-2.5 text-xs" onClick={() => toggleAllVisibleEmployees(!allVisibleEmployeesSelected)} data-testid="payroll-employees-select-all-visible-button">
              {allVisibleEmployeesSelected ? 'Limpar visíveis' : 'Seleccionar visíveis'}
            </Button>
            <Button variant="outline" size="sm" className="h-8 rounded-lg px-2.5 text-xs" disabled={!selectedEmployeeIds.length} onClick={handleBulkProcessEmployees} data-testid="payroll-employees-bulk-process-button">
              Processar seleccionados
            </Button>
          </div>

          <div className="flex flex-wrap justify-end gap-1.5 text-[11px]" data-testid="payroll-employees-directory-badges">
            <Badge variant="outline" className="bg-white">Ordenação: {employeeSortOptions.find((option) => option.value === employeeSortField)?.label || employeeSortField}</Badge>
            <Badge variant="outline" className="bg-white">Direcção: {employeeSortDirection === 'asc' ? 'Ascendente' : 'Descendente'}</Badge>
            <Badge variant="outline" className="bg-white">Incompletos: {employeeDirectory.incomplete}</Badge>
          </div>
        </div>
      </div>
    </CardHeader>
    <CardContent className="p-0">
      {sortedEmployees.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground" data-testid="payroll-employees-empty-state">
          Nenhum colaborador corresponde aos filtros activos.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1120px]">
            <thead className="sticky top-0 z-10 border-b bg-slate-50/95 backdrop-blur">
              <tr>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  <Checkbox checked={allVisibleEmployeesSelected} onCheckedChange={(checked) => toggleAllVisibleEmployees(Boolean(checked))} data-testid="payroll-employees-select-all-checkbox" />
                </th>
                <th className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Nr.</th>
                <th className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Nome do colaborador</th>
                <th className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Perfil / Função</th>
                <th className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Admissão</th>
                <th className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Telefone</th>
                <th className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Aniversário</th>
                <th className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Loja</th>
                <th className="px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Salário base</th>
                <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Acções</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedEmployees.map((employee) => (
                <tr key={employee.id} className={cn('hover:bg-slate-50/50', selectedEmployeeIds.includes(employee.id) && 'bg-slate-100/80')} data-testid={`payroll-employee-row-${employee.id}`}>
                  <td className="px-3 py-1.5 align-middle">
                    <Checkbox checked={selectedEmployeeIds.includes(employee.id)} onCheckedChange={(checked) => toggleEmployeeSelection(employee.id, Boolean(checked))} disabled={employee.payroll_eligible === false} data-testid={`payroll-employee-select-checkbox-${employee.id}`} />
                  </td>
                  <td className="px-2 py-1.5 align-middle font-mono text-[12px] font-semibold text-slate-700" data-testid={`payroll-employee-number-${employee.id}`}>
                    {employee.employee_number || '—'}
                  </td>
                  <td className="px-2 py-1.5 align-middle" data-testid={`payroll-employee-name-${employee.id}`}>
                    <div className="space-y-1">
                      <p className="text-[13px] font-semibold leading-none text-slate-900">{employee.full_name || employee.name}</p>
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge className={cn('rounded-full px-1.5 py-0 text-[10px]', employee.dossier_complete ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700')}>
                          {employee.dossier_complete ? 'Completo' : 'Em falta'}
                        </Badge>
                        <Badge variant="outline" className={cn('rounded-full px-1.5 py-0 text-[10px]', employee.employment_state === 'offboarded' ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700')} data-testid={`payroll-employee-status-${employee.id}`}>
                          {employee.employment_state === 'offboarded' ? 'Saído / Suspenso' : 'Activo'}
                        </Badge>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-1.5 align-middle text-xs text-slate-700" data-testid={`payroll-employee-role-${employee.id}`}>{employee.job_title || employee.profile_role_label || employee.role || '—'}</td>
                  <td className="px-2 py-1.5 align-middle text-xs text-slate-700" data-testid={`payroll-employee-hire-date-${employee.id}`}>{formatCompactDate(employee.hire_date, true)}</td>
                  <td className="px-2 py-1.5 align-middle text-xs text-slate-700" data-testid={`payroll-employee-phone-${employee.id}`}>{employee.phone || '—'}</td>
                  <td className="px-2 py-1.5 align-middle text-xs text-slate-700" data-testid={`payroll-employee-birthday-${employee.id}`}>{formatCompactDate(employee.birth_date, false)}</td>
                  <td className="px-2 py-1.5 align-middle text-xs text-slate-700" data-testid={`payroll-employee-store-${employee.id}`}>{employee.store_location || '—'}</td>
                  <td className="px-2 py-1.5 text-right align-middle text-xs font-semibold text-slate-900" data-testid={`payroll-employee-salary-${employee.id}`}>{formatCurrency(employee.base_salary || 0)}</td>
                  <td className="px-2 py-1.5 align-middle">
                    <div className="flex justify-center">
                      <Button variant="ghost" size="sm" className="h-7 rounded-lg px-2 text-slate-600" onClick={() => openDossierDialog(employee)} data-testid={`payroll-employee-open-${employee.id}`}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>
);